// The All page's data side: resources flattened to what the query reads
// (allQuery.ts's Subject), and the saved tabs kept in Settings.
import type { Edge } from '@/db/types'
import type { TaskResource } from '@/kinds/task/apply'
import type { ProjectResource } from '@/kinds/project/apply'
import type { PageResource } from '@/kinds/page/apply'
import type { ListResource } from '@/kinds/list/apply'
import { isOpen } from '@/kinds/task/schema'
import { isProjectOpen } from '@/kinds/project/schema'
import { dateOf } from './time'
import { parseQuery, type ItemKind, type Subject } from './allQuery'

/** The local day of an RFC 3339 timestamp. */
const dayOf = (iso: string): string => {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso.slice(0, 10) : dateOf(d)
}

const base = (r: { labels: Record<string, string>; created_at: string; updated_at: string }) => ({
  ws: r.labels.ws ?? '',
  labels: r.labels,
  updated: dayOf(r.updated_at),
  created: dayOf(r.created_at),
})

export interface Sources {
  tasks: TaskResource[]
  projects: ProjectResource[]
  pages: PageResource[]
  lists: ListResource[]
  edges: Edge[]
}

/** Every live task, project, page and list as a Subject, each with the title of the project it belongs to. */
export const subjectsOf = ({ tasks, projects, pages, lists, edges }: Sources): Subject[] => {
  const titleOf = new Map(projects.map((p) => [p.id, p.spec.title]))
  const projectOf = new Map<string, string>()
  for (const e of edges)
    if (e.relation === 'belongs_to' && !e.deleted_at && titleOf.has(e.to_id))
      projectOf.set(e.from_id, titleOf.get(e.to_id)!)
  const out: Subject[] = []
  for (const t of tasks) {
    if (t.deleted_at) continue
    out.push({
      ...base(t),
      kind: 'task',
      id: t.id,
      title: t.spec.title,
      body: t.spec.notes ?? '',
      state: t.status.state,
      open: isOpen(t.status.state) && !t.status.converted_to,
      template: !!t.spec.recurrence,
      pinned: false,
      project: projectOf.get(t.id),
      priority: t.spec.priority,
      due: t.spec.due,
      scheduled: t.spec.scheduled,
    })
  }
  for (const p of projects) {
    if (p.deleted_at) continue
    out.push({
      ...base(p),
      kind: 'project',
      id: p.id,
      title: p.spec.title,
      body: [p.spec.notes, p.spec.outcome].filter(Boolean).join('\n'),
      state: p.status.state,
      open: isProjectOpen(p.status.state) && !p.status.converted_to,
      template: false,
      pinned: !!p.spec.pinned,
      project: projectOf.get(p.id),
      due: p.spec.due,
    })
  }
  for (const n of pages) {
    if (n.deleted_at) continue
    out.push({
      ...base(n),
      kind: 'page',
      id: n.id,
      title: n.name,
      body: n.spec.text,
      open: true,
      template: false,
      pinned: !!n.spec.pinned,
      project: projectOf.get(n.id),
    })
  }
  for (const l of lists) {
    if (l.deleted_at) continue
    out.push({
      ...base(l),
      kind: 'list',
      id: l.id,
      title: l.spec.title,
      body: l.spec.items.map((i) => i.text).join('\n'),
      // a converted list left the index
      open: !l.status.converted_to,
      template: false,
      pinned: !!l.spec.pinned,
      project: projectOf.get(l.id),
    })
  }
  return out
}

// ---- tabs -----------------------------------------------------------------

/** The four tabs every device has; each is a kind and nothing else. */
export const BUILT_IN_TABS: { id: ItemKind; label: string }[] = [
  { id: 'task', label: 'Tasks' },
  { id: 'project', label: 'Projects' },
  { id: 'page', label: 'Pages' },
  { id: 'list', label: 'Lists' },
]
export const isBuiltIn = (id: string): id is ItemKind => BUILT_IN_TABS.some((t) => t.id === id)

/** A query the owner pinned as a tab (Settings `all_tabs`). */
export interface SavedTab {
  id: string
  name: string
  q: string
}
/** The Settings schema's caps. */
export const MAX_SAVED_TABS = 12
export const MAX_TAB_NAME = 40
export const MAX_TAB_QUERY = 300

/** A short id for a new tab. */
export const newTabId = (taken: readonly string[]): string => {
  for (;;) {
    const id = Math.random().toString(36).slice(2, 7)
    if (id && !taken.includes(id)) return id
  }
}

/** A name for a query: its first words, trimmed to fit. */
export const nameFor = (q: string): string => {
  const t = q.trim().replace(/\s+/g, ' ')
  return t.length > 24 ? `${t.slice(0, 23)}…` : t
}

/**
 * The tab list after pinning `q`. A query pinned from a kind's tab keeps that
 * kind (`kind:task` is put in front unless it already says one), so the pinned
 * tab shows what the page showed. Past the cap the pin is refused (null).
 */
export const pin = (tabs: SavedTab[], from: string, q: string, name: string): SavedTab[] | null => {
  if (tabs.length >= MAX_SAVED_TABS) return null
  const parsed = parseQuery(q)
  if (!parsed.ok) return null
  const says = /(^|[\s(-])kind[:=]/i.test(q)
  const body = isBuiltIn(from) && !says ? `kind:${from} ${q.trim()}`.trim() : q.trim()
  const id = newTabId(tabs.map((t) => t.id))
  return [
    ...tabs,
    {
      id,
      name: (name.trim() || nameFor(q) || 'Saved').slice(0, MAX_TAB_NAME),
      q: body.slice(0, MAX_TAB_QUERY),
    },
  ]
}

/** What Settings `all_tabs` holds, repaired: only well-formed tabs, no repeats, within the caps. */
export const tabsOf = (raw: unknown): SavedTab[] => {
  if (!Array.isArray(raw)) return []
  const seen = new Set<string>()
  const out: SavedTab[] = []
  for (const x of raw) {
    if (!x || typeof x !== 'object') continue
    const { id, name, q } = x as Record<string, unknown>
    if (typeof id !== 'string' || !id || seen.has(id) || isBuiltIn(id)) continue
    if (typeof name !== 'string' || typeof q !== 'string') continue
    seen.add(id)
    out.push({ id, name: name.slice(0, MAX_TAB_NAME), q: q.slice(0, MAX_TAB_QUERY) })
  }
  return out.slice(0, MAX_SAVED_TABS)
}
